import { create } from 'zustand'
import { dbgEvent } from '../lib/debug'

/**
 * Linger d'AFFICHAGE d'une tache terminale (ms). Une tache terminale (fin connue OU reconciliee)
 * reste affichee ce court instant pour montrer son statut final (completed/killed/failed), puis sa
 * ligne est retiree. COSMETIQUE — PAS un timeout de vivacite : la vivacite est determinee par les
 * events (background_tasks REPLACE + progressions), jamais par un silence. Timer local par tache,
 * nettoye a chaque transition et au reset de session (un timer non nettoye fuit). Valeur initiale
 * courte, changeable en une ligne.
 */
export const TASK_TERMINAL_LINGER_MS = 5_000

/** Statuts terminaux : la tache ne progresse plus, sa duree est figee, un linger la retire. */
const TERMINAL_STATUSES = new Set(['completed', 'failed', 'killed', 'stopped', 'lost'])
function isTerminal(status: string): boolean {
  return TERMINAL_STATUSES.has(status)
}

/**
 * Store des taches (sous-agents / Cowboys) — reconception du suivi (operation #31513).
 *
 * PROJECTION des events du SDK : on ne fabrique AUCUNE donnee localement. Duree, fin et vivacite se
 * LISENT des events. Il n'existe plus de chronometre local (`startedAtMs` + `Date.now() - ...`) ni de
 * timeout de vivacite (`TASK_EXPIRE_MS`/`sweepExpired`) — leurs deux defauts constates en recette (la
 * duree repartait a zero a la recreation de la ligne, et le timeout faisait clignoter les taches
 * vivantes pendant les trous d'emission mesures jusqu'a 149 s). La duree est `usage.duration_ms`
 * AFFICHE TEL QUEL, jamais recalcule.
 *
 * Indexe par sessionId PUIS par task_id. Il est le mecanisme PARALLELE au suivi interne de
 * TerminalView (useRef), jamais un deplacement de celui-ci (duplication assumee, decision Fabrice).
 *
 * ECRIVAINS (brief §4bis etage 6) :
 *  - `terminalHandler` ecrit le DEBUT et la FIN de tache (variantes terminal enrichies, regime A,
 *    persistees) : startTask / notifyTask / updateTask ; et remet la session a zero au (re)demarrage
 *    (resetSession, sur system_init).
 *  - `tasksHandler` ecrit les PROGRESSIONS (etiquette task_progress, regime B) : progressTask.
 *  - `backgroundTasksHandler` applique la RECONCILIATION (etiquette background_tasks, regime B,
 *    semantique REPLACE) : reconcile.
 * AUCUN composant React n'ecrit ce store.
 *
 * Cycle de vie PAR LES EVENTS (pas par un timer de silence) : une tache est vivante tant qu'elle est
 * dans le dernier background_tasks OU qu'elle emet des progressions ; sa fin est connue par un
 * task_updated{status terminal}/task_notification, OU par reconciliation (REPLACE : tout task_id
 * absent du nouvel ensemble et non deja terminal → « lost »). Une tache terminale est retiree apres
 * TASK_TERMINAL_LINGER_MS. Les actions upsert (creent l'entree si absente) : l'ordre d'arrivee
 * start/progress/fin n'est pas garanti.
 */

export interface TaskState {
  taskId: string
  /** Nom d'appel STABLE : description du task_start, a defaut le type de sous-agent. */
  name: string | null
  subagentType: string | null
  /** Activite courante : description du dernier task_progress (change a chaque tick). */
  currentActivity: string | null
  /** Dernier outil utilise (last_tool_name du SDK). */
  lastToolName: string | null
  /** Duree reelle cumulee du sous-agent (usage.duration_ms du SDK). AFFICHEE TELLE QUELLE. */
  durationMs: number | null
  /** Nombre d'outils utilises (usage.tool_uses du SDK). */
  toolUses: number | null
  /** Tokens cumules (usage.total_tokens du SDK — CACHE INCLUS, pas de breakdown hors-cache). */
  totalTokens: number | null
  /** Statut : running | pending | paused | completed | failed | stopped | killed | lost. Porte killed/failed. */
  status: string
  /** Fin reelle (end_time du task_updated terminal, epoch ms) quand presente. */
  endTime: number | null
}

interface TasksStoreState {
  sessions: Record<string, Record<string, TaskState>>
  /** [terminalHandler] Debut de tache (variante terminal task_start enrichie). */
  startTask: (
    sessionId: string,
    taskId: string,
    fields: { description?: string; subagent_type?: string; task_type?: string },
  ) => void
  /** [terminalHandler] Notification de tache (variante terminal task_notification enrichie) — fin. */
  notifyTask: (
    sessionId: string,
    taskId: string,
    fields: { status?: string; summary?: string; usage?: { total_tokens: number; tool_uses: number; duration_ms: number } },
  ) => void
  /** [terminalHandler] Transition de tache (variante terminal task_updated) — porte killed/failed + end_time. */
  updateTask: (
    sessionId: string,
    taskId: string,
    fields: { status?: string; is_backgrounded?: boolean; error?: string; end_time?: number },
  ) => void
  /** [tasksHandler] Progression de tache (etiquette task_progress, regime B). */
  progressTask: (
    sessionId: string,
    taskId: string,
    fields: { description?: string; subagent_type?: string; usage?: { total_tokens: number; tool_uses: number; duration_ms: number }; last_tool_name?: string; summary?: string },
  ) => void
  /**
   * [backgroundTasksHandler] Reconciliation REPLACE (etiquette background_tasks, regime B) : marque
   * « lost » (terminal) toute tache de la session ABSENTE de `aliveTaskIds` et pas deja terminale.
   * C'est le remplacement du timeout de silence : ce qui n'est plus dans l'ensemble vivant est fini.
   */
  reconcile: (sessionId: string, aliveTaskIds: string[]) => void
  /**
   * [terminalHandler] Reset des taches d'une session au (re)demarrage (system_init) : background_tasks
   * est per-worker et se vide au (re)demarrage du CLI, sinon un indicateur reste coince.
   */
  resetSession: (sessionId: string) => void
  /** Retire une tache (utilise par le linger d'affichage et le reset). */
  removeTask: (sessionId: string, taskId: string) => void
}

function emptyTask(taskId: string): TaskState {
  return {
    taskId,
    name: null,
    subagentType: null,
    currentActivity: null,
    lastToolName: null,
    durationMs: null,
    toolUses: null,
    totalTokens: null,
    status: 'running',
    endTime: null,
  }
}

/** Applique un patch immuable a la tache (sessionId, taskId), en la creant si absente. */
function patchTask(
  state: TasksStoreState,
  sessionId: string,
  taskId: string,
  mutate: (task: TaskState) => TaskState,
): Pick<TasksStoreState, 'sessions'> {
  const sessionTasks = state.sessions[sessionId] ?? {}
  const existing = sessionTasks[taskId] ?? emptyTask(taskId)
  return {
    sessions: {
      ...state.sessions,
      [sessionId]: {
        ...sessionTasks,
        [taskId]: mutate(existing),
      },
    },
  }
}

/** Retire (sessionId, taskId) des sessions, sans muter (meme reference si rien a retirer). */
function removeFromSessions(
  sessions: TasksStoreState['sessions'],
  sessionId: string,
  taskId: string,
): TasksStoreState['sessions'] {
  const sessionTasks = sessions[sessionId]
  if (!sessionTasks || !(taskId in sessionTasks)) return sessions
  const next = { ...sessionTasks }
  delete next[taskId]
  return { ...sessions, [sessionId]: next }
}

// Timers de linger d'affichage, au niveau module (un par tache terminale). Cle = sessionId::taskId.
// Ce ne sont PAS des timeouts de vivacite : ils ne font que retirer la ligne apres que le statut
// final a ete montre un court instant. La vivacite vit dans les events, jamais ici.
const lingerTimers = new Map<string, ReturnType<typeof setTimeout>>()
const timerKey = (sessionId: string, taskId: string): string => `${sessionId}::${taskId}`

export const useTasksStore = create<TasksStoreState>((set, get) => {
  const clearLinger = (sessionId: string, taskId: string): void => {
    const key = timerKey(sessionId, taskId)
    const t = lingerTimers.get(key)
    if (t !== undefined) {
      clearTimeout(t)
      lingerTimers.delete(key)
    }
  }

  const scheduleLinger = (sessionId: string, taskId: string): void => {
    clearLinger(sessionId, taskId)
    const key = timerKey(sessionId, taskId)
    const timer = setTimeout(() => {
      lingerTimers.delete(key)
      set((state) => ({ sessions: removeFromSessions(state.sessions, sessionId, taskId) }))
    }, TASK_TERMINAL_LINGER_MS)
    // unref cote Node (tests d'isolation) : le timer ne retient pas le process. No-op cote
    // navigateur (setTimeout y renvoie un number sans .unref), d'ou l'appel optionnel garde.
    ;(timer as unknown as { unref?: () => void }).unref?.()
    lingerTimers.set(key, timer)
  }

  /** Programme le retrait si le statut est terminal, l'annule sinon (la tache a repris vie). */
  const syncLinger = (sessionId: string, taskId: string, status: string): void => {
    if (isTerminal(status)) scheduleLinger(sessionId, taskId)
    else clearLinger(sessionId, taskId)
  }

  return {
    sessions: {},

    startTask: (sessionId, taskId, fields) => {
      dbgEvent('Zustand', 'tasks.startTask', { sessionId: sessionId.slice(0, 8), taskId })
      set((state) =>
        patchTask(state, sessionId, taskId, (task) => ({
          ...task,
          name: fields.description ?? fields.subagent_type ?? task.name,
          subagentType: fields.subagent_type ?? task.subagentType,
          status: 'running',
        })),
      )
      clearLinger(sessionId, taskId)
    },

    notifyTask: (sessionId, taskId, fields) => {
      dbgEvent('Zustand', 'tasks.notifyTask', { sessionId: sessionId.slice(0, 8), taskId, status: fields.status })
      const nextStatus = fields.status ?? get().sessions[sessionId]?.[taskId]?.status ?? 'running'
      set((state) =>
        patchTask(state, sessionId, taskId, (task) => ({
          ...task,
          status: fields.status ?? task.status,
          durationMs: fields.usage?.duration_ms ?? task.durationMs,
          totalTokens: fields.usage?.total_tokens ?? task.totalTokens,
          toolUses: fields.usage?.tool_uses ?? task.toolUses,
        })),
      )
      syncLinger(sessionId, taskId, nextStatus)
    },

    updateTask: (sessionId, taskId, fields) => {
      dbgEvent('Zustand', 'tasks.updateTask', { sessionId: sessionId.slice(0, 8), taskId, status: fields.status })
      const nextStatus = fields.status ?? get().sessions[sessionId]?.[taskId]?.status ?? 'running'
      set((state) =>
        patchTask(state, sessionId, taskId, (task) => ({
          ...task,
          status: fields.status ?? task.status,
          endTime: fields.end_time ?? task.endTime,
        })),
      )
      syncLinger(sessionId, taskId, nextStatus)
    },

    progressTask: (sessionId, taskId, fields) => {
      dbgEvent('Zustand', 'tasks.progressTask', { sessionId: sessionId.slice(0, 8), taskId, tool: fields.last_tool_name })
      // Une progression est un battement de vivacite : si la tache avait ete reconciliee « lost »,
      // elle reprend vie (running) et son retrait programme est annule.
      const wasLost = get().sessions[sessionId]?.[taskId]?.status === 'lost'
      set((state) =>
        patchTask(state, sessionId, taskId, (task) => ({
          ...task,
          name: task.name ?? fields.description ?? fields.subagent_type ?? null,
          subagentType: task.subagentType ?? fields.subagent_type ?? null,
          currentActivity: fields.description ?? task.currentActivity,
          lastToolName: fields.last_tool_name ?? task.lastToolName,
          durationMs: fields.usage?.duration_ms ?? task.durationMs,
          totalTokens: fields.usage?.total_tokens ?? task.totalTokens,
          toolUses: fields.usage?.tool_uses ?? task.toolUses,
          status: task.status === 'lost' ? 'running' : task.status,
        })),
      )
      if (wasLost) clearLinger(sessionId, taskId)
    },

    reconcile: (sessionId, aliveTaskIds) => {
      dbgEvent('Zustand', 'tasks.reconcile', { sessionId: sessionId.slice(0, 8), alive: aliveTaskIds.length })
      const alive = new Set(aliveTaskIds)
      const toLinger: string[] = []
      set((state) => {
        const sessionTasks = state.sessions[sessionId]
        if (!sessionTasks) return state
        let changed = false
        const next: Record<string, TaskState> = { ...sessionTasks }
        for (const [taskId, task] of Object.entries(sessionTasks)) {
          if (!alive.has(taskId) && !isTerminal(task.status)) {
            next[taskId] = { ...task, status: 'lost' }
            toLinger.push(taskId)
            changed = true
          }
        }
        if (!changed) return state
        return { sessions: { ...state.sessions, [sessionId]: next } }
      })
      for (const taskId of toLinger) scheduleLinger(sessionId, taskId)
    },

    resetSession: (sessionId) => {
      dbgEvent('Zustand', 'tasks.resetSession', { sessionId: sessionId.slice(0, 8) })
      // Annuler tous les timers de linger de la session : sinon un timer survivrait au reset et
      // retirerait plus tard une tache de la NOUVELLE generation.
      const prefix = `${sessionId}::`
      for (const key of Array.from(lingerTimers.keys())) {
        if (key.startsWith(prefix)) {
          clearTimeout(lingerTimers.get(key)!)
          lingerTimers.delete(key)
        }
      }
      set((state) => {
        if (!(sessionId in state.sessions)) return state
        return { sessions: { ...state.sessions, [sessionId]: {} } }
      })
    },

    removeTask: (sessionId, taskId) => {
      clearLinger(sessionId, taskId)
      set((state) => ({ sessions: removeFromSessions(state.sessions, sessionId, taskId) }))
    },
  }
})
